import { ICliente } from "../../entities/catalogos/ICliente";
import { MercanciaDatosComplementarios } from "../../entities/solicitudes/MercanciaDatosComplementarios";
import { IParametro } from "../../redux/types/parametroActionTypes";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { enums } from "../../enums/enums";
import { AreaServicio } from "../../entities/catalogos/AreaServicio";

export const CLASIFICACION_AS_PUERTO = 4;
export const CLASIFICACION_AS_CINCO = 5;

export function esClasificacionComplementariosCartaPorte(clasificacionId?: number): boolean {
    return clasificacionId === CLASIFICACION_AS_PUERTO || clasificacionId === CLASIFICACION_AS_CINCO;
}

export function obtenerClasificacionDesdeAreaServicio(areaServicio?: AreaServicio | null): number | undefined {
    if (!areaServicio) {
        return undefined;
    }
    const raw = (areaServicio as AreaServicio & { clasificacionId?: number }).clasificacionID
        ?? (areaServicio as AreaServicio & { clasificacionId?: number }).clasificacionId;
    if (raw === undefined || raw === null) {
        return undefined;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? undefined : parsed;
}

export function obtenerCodigoPostalRecoleccionGuia(recoleccion: IRecoleccion): string {
    const cp = recoleccion.codigoPostal?.trim()
        || recoleccion.distribuidor?.codigoPostal?.trim()
        || recoleccion.colonia?.codigoPostal?.trim()
        || '';
    return cp;
}

export function esCartaPorteDetalleActivo(cliente: ICliente): boolean {
    return !!(cliente.habilitarDatosCompCartaPorteDetalle
        || cliente.clienteConfiguracionCompCartaPorteDetalle?.activo);
}

export function debeMostrarDatosComplementariosGuia(
    cartaPorteDetalleActivo: boolean,
    cliente: ICliente,
    clasificacionOrigenId?: number,
    clasificacionDestinoId?: number
): boolean {
    return esClasificacionComplementariosCartaPorte(clasificacionOrigenId)
        || esClasificacionComplementariosCartaPorte(clasificacionDestinoId)
        || cartaPorteDetalleActivo
        || !!(cliente.clienteConfiguracionComplementoCartaPorte?.activo);
}

export function esPaisEuRecoleccionGuia(recoleccion: IRecoleccion): boolean {
    const distribuidor = recoleccion.distribuidor;
    if (distribuidor?.pais?.nombre === 'EU') {
        return true;
    }
    if (distribuidor?.colonia?.pais?.nombre === 'EU') {
        return true;
    }
    if (recoleccion.colonia?.pais?.nombre === 'EU') {
        return true;
    }
    return false;
}

export function requiereValidacionEstrictaComplementarios(
    parametros: IParametro[],
    cartaPorteDetalleActivo: boolean,
    clasificacionOrigenId?: number,
    clasificacionDestinoId?: number
): boolean {
    const param = parametros.find(p => p.id === enums.parametro.AplicaAreaServicioPuertoAeropuertoCartaPorte);
    if (param?.valor?.toLocaleLowerCase() !== 'true') {
        return false;
    }
    return esClasificacionComplementariosCartaPorte(clasificacionOrigenId)
        || esClasificacionComplementariosCartaPorte(clasificacionDestinoId)
        || cartaPorteDetalleActivo;
}

export function puedeAvanzarComplementariosSinValidacionEstricta(cliente: ICliente): boolean {
    return !!cliente.habilitarDatosComplementariosCartaPorte && !cliente.habilitarDatosCompCartaPorteDetalle;
}

export interface ValidacionComplementariosResult {
    ok: boolean;
    messageId?: number;
}

function filaComplementariaVacia(mercancia: MercanciaDatosComplementarios): boolean {
    return !mercancia.cantidad
        && !mercancia.claveUnidadId
        && !mercancia.claveProductoId
        && !mercancia.descripcionMercancia
        && !mercancia.claveFraccionArancelariaId
        && !mercancia.uuidComprobante
        && (mercancia.peso == null || mercancia.peso === 0);
}

export function validarComplementariosGuia(
    mercancias: MercanciaDatosComplementarios[],
    cliente: ICliente,
    parametros: IParametro[],
    cartaPorteDetalleActivo: boolean,
    origenEu: boolean,
    destinoEu: boolean,
    clasificacionOrigenId?: number,
    clasificacionDestinoId?: number
): ValidacionComplementariosResult {
    const datosObligatorios = !!cliente.habilitarDatosCompCartaPorteDetalle;
    const param = parametros.find(p => p.id === enums.parametro.AplicaAreaServicioPuertoAeropuertoCartaPorte);
    const param126Activo = param?.valor?.toLocaleLowerCase() === 'true';
    const clasificacionCartaPorte = esClasificacionComplementariosCartaPorte(clasificacionOrigenId)
        || esClasificacionComplementariosCartaPorte(clasificacionDestinoId)
        || cartaPorteDetalleActivo;

    if (param126Activo && clasificacionCartaPorte) {
        return validarComplementariosEstricto(mercancias, true, origenEu, destinoEu);
    }

    if (cartaPorteDetalleActivo && !mercancias.length) {
        return { ok: false, messageId: 101 };
    }

    if (puedeAvanzarComplementariosSinValidacionEstricta(cliente)) {
        return { ok: true };
    }

    if (param126Activo && datosObligatorios && !mercancias.length) {
        return { ok: false, messageId: 101 };
    }

    for (const mercancia of mercancias) {
        if (!mercancia.cantidad || !mercancia.claveUnidadId || !mercancia.claveProductoId) {
            return { ok: false, messageId: 101 };
        }

        for (const campo of mercancia.camposParaValidar ?? []) {
            if (mercancia[campo] == null) {
                return { ok: false, messageId: 101 };
            }
            if (campo === 'peso' && mercancia[campo] === 0) {
                return { ok: false, messageId: 77 };
            }
            if (!mercancia.claveFraccionArancelariaId && origenEu) {
                return { ok: false, messageId: 45 };
            }
            if (!mercancia.uuidComprobante && origenEu) {
                return { ok: false, messageId: 46 };
            }
            if (!mercancia.claveFraccionArancelariaId && destinoEu) {
                return { ok: false, messageId: 45 };
            }
            if (!mercancia.uuidComprobante && destinoEu) {
                return { ok: false, messageId: 46 };
            }
        }

        if (mercancia.claveProducto != null && mercancia.claveProducto.materialPeligroso === '1') {
            return { ok: false, messageId: 135 };
        }

        if (!cliente.habilitarDatosCompCartaPorteDetalle && filaComplementariaVacia(mercancia)) {
            continue;
        }
    }

    return { ok: true };
}

function validarComplementariosEstricto(
    mercancias: MercanciaDatosComplementarios[],
    datosObligatorios: boolean,
    origenEu: boolean,
    destinoEu: boolean
): ValidacionComplementariosResult {
    if (datosObligatorios && !mercancias.length) {
        return { ok: false, messageId: 101 };
    }

    for (const mercancia of mercancias) {
        if (!mercancia.cantidad || !mercancia.claveUnidadId || !mercancia.claveProductoId) {
            return { ok: false, messageId: 101 };
        }

        for (const campo of mercancia.camposParaValidar ?? []) {
            if (campo === 'cantidad' && mercancia[campo] === 0) {
                return { ok: false, messageId: 311 };
            }
            if (campo === 'claveUnidadId' && mercancia[campo] === '') {
                return { ok: false, messageId: 312 };
            }
            if (campo === 'claveProductoId' && mercancia[campo] === '') {
                return { ok: false, messageId: 313 };
            }
            if (campo === 'descripcionMercancia' && mercancia[campo] === '') {
                return { ok: false, messageId: 314 };
            }
            if (campo === 'peso' && mercancia[campo] === 0) {
                return { ok: false, messageId: 77 };
            }
        }

        if (mercancia.claveProducto != null && mercancia.claveProducto.materialPeligroso === '1') {
            return { ok: false, messageId: 135 };
        }

        if (!mercancia.claveFraccionArancelariaId && origenEu) {
            return { ok: false, messageId: 45 };
        }
        if (!mercancia.uuidComprobante && origenEu) {
            return { ok: false, messageId: 46 };
        }
        if (!mercancia.claveFraccionArancelariaId && destinoEu) {
            return { ok: false, messageId: 45 };
        }
        if (!mercancia.uuidComprobante && destinoEu) {
            return { ok: false, messageId: 46 };
        }
    }

    return { ok: true };
}
